import { computed, ref, watch, type ComputedRef, type Ref } from 'vue'
import type { ChartType, ReportViewType } from '../models/chart.types'
import type { ResolvedDataVisibleProps } from '../models/dataVisible.types'
import type { ReportFilter } from '../models/filter.types'
import type { ReportTableSortV1 } from '../models/tableConfig.types'
import type { ReportCardStatus, ReportTableType } from '../models/tablePresentation.types'
import type { TrendTableFrequency } from '../models/trendTable.types'
import { mapColumns } from '../service/mapColumns'
import { toReportColumnOption } from '../service/reportColumnOption'
import { resolveReportPagination } from '../service/reportPagination'
import { specRequestsComparison } from '../service/reportQueryString'
import {
  cloneResolvedTrend,
  resolveDataVisibleTrendRequest,
} from './resolveDataVisibleTrendRequest'
import { useDataVisibleChartContract } from './useDataVisibleChartContract'
import { useDataVisibleChartRequest } from './useDataVisibleChartRequest'
import { unique, useDataVisibleChartState } from './useDataVisibleChartState'
import { useDataVisibleExport } from './useDataVisibleExport'
import { useDataVisibleReportRequest } from './useDataVisibleReportRequest'
import { useDataVisibleTableExperience } from './useDataVisibleTableExperience'
import { useElementFullscreen } from './useElementFullscreen'
import { useGa4Override } from './useGa4Override'
import { useMetricCatalog } from './useMetricCatalog'
import { useReportCellFormatter } from './useReportCellFormatter'
import { useReportData } from './useReportData'
import { useReportTableRender } from './useReportTableRender'
import { useSettledReportRenderSnapshot } from './useSettledReportRenderSnapshot'
import { useI18n } from '@/shared/i18n'
import { useToast } from '@/shared/ui'
import type { UiAsyncState } from '@/shared/ui/types'
export interface DataVisibleEmit {
  (event: 'update:view', value: ReportViewType): void
  (event: 'update:chartType', value: ChartType): void
  (event: 'update:sort', value: ReportTableSortV1 | null): void
  (event: 'update:columnOrder', value: string[]): void
  (event: 'update:columnWidths', value: Record<string, number>): void
  (event: 'update:userFilters', value: ReportFilter[]): void
  (event: 'update:tableType', value: ReportTableType): void
  (event: 'update:breakdown', value: string | null): void
  (event: 'update:frequency', value: TrendTableFrequency): void
}
/** DataVisible data, presentation and export orchestration (contract 08 §10). */
export function useDataVisible(
  props: ResolvedDataVisibleProps,
  emit: DataVisibleEmit,
  surface: Ref<HTMLElement | null>,
) {
  const { t } = useI18n()
  const toast = useToast()
  const { getColumn, metricLabel } = useMetricCatalog()
  const { formatCell } = useReportCellFormatter()
  const fullscreen = useElementFullscreen(surface)
  const chartTable = computed(() => props.composition === 'chart-table')
  // Offer all available fields, but seed non-selected ones hidden for the customize panel.
  const preselectedFields = computed(() => new Set([...props.dimensions, ...props.metrics]))
  const offeredFields = computed(() =>
    props.availableFields?.length ? props.availableFields : [...preselectedFields.value],
  )
  const mappedColumns = computed(() =>
    mapColumns(offeredFields.value, { getColumn }).map((column) =>
      preselectedFields.value.has(column.field) && !props.hideCols?.includes(column.field)
        ? column
        : { ...column, invisible: true },
    ),
  )
  const chartDimensions = computed(() => props.chartDimensions ?? props.dimensions)
  const chartMetrics = computed(() => props.chartMetrics ?? props.metrics)
  // Offer every table field (not just a hardcoded chart default) as a chart-field source.
  const tableFields = (numeric: boolean): string[] =>
    mappedColumns.value.filter((column) => Boolean(column.numeric) === numeric).map((c) => c.field)
  const fieldSourceDimensions = computed(() =>
    unique([...chartDimensions.value, ...tableFields(false)]),
  )
  const fieldSourceMetrics = computed(() => unique([...chartMetrics.value, ...tableFields(true)]))
  const inferredTimeDimensions = computed(() =>
    props.timeDimensions?.length
      ? props.timeDimensions
      : chartDimensions.value.filter((field) => {
          const column = getColumn(field)
          return column?.date_col || column?.sub_type === 'date' || column?.sub_type === 'datetime'
        }),
  )
  // Comparison-only chart types are offered once the controller is asked for compare data.
  const hasComparison = computed(() =>
    specRequestsComparison({
      ...(props.defaultPostData === undefined ? {} : { defaultPostData: props.defaultPostData }),
      ...(props.addCompare ? { add_compare: true } : {}),
    }),
  )
  const chart = useDataVisibleChartState({
    id: () => props.id,
    partitionName: () => props.partitionName,
    defaultView: () => props.view,
    defaultChartType: () => props.chartType,
    allowedViews: () => props.allowedViews,
    allowedChartTypes: () => props.allowedChartTypes,
    dimensions: chartDimensions,
    metrics: chartMetrics,
    fieldSourceDimensions,
    fieldSourceMetrics,
    timeDimensions: inferredTimeDimensions,
    hasComparison,
    fieldLabel: metricLabel,
    fieldMeta: (field) =>
      getColumn(field) ? toReportColumnOption(getColumn(field)!).meta : undefined,
  })
  const stableViewEnabled = computed(() => !props.readOnly)
  const tableFeaturesEnabled = computed(
    () =>
      stableViewEnabled.value &&
      (chartTable.value || chart.configuredViews.value.includes('table')),
  )
  const {
    table,
    presentation,
    presentedColumns,
    availableTableTypes,
    effectiveTableType,
    matrixAssignment,
    trendAssignment,
    tableUnavailable,
    savedFilters,
    dropdownables,
    loadDropdownValues,
    savedViews,
    savedViewId,
    filterDraft,
    activeUserFilters,
    customizeOpen,
    applyCustomization,
    updateTableType,
    updateBreakdown,
    updateFrequency,
    applyFilters,
    saveFilter,
    removeFilter,
    saveNamedView,
    selectNamedView,
    renameNamedView,
    removeNamedView,
    updateSort: updateTableSort,
    updateColumnOrder,
    updateColumnWidths,
    updateFrozenField,
    updateExpandedField,
  } = useDataVisibleTableExperience({
    id: () => props.id,
    reportId: () => props.reportId,
    partitionName: () => props.partitionName,
    sortField: () => props.sortField,
    sortOrder: () => props.sortOrder,
    tableConfig: () => props.tableConfig,
    columns: mappedColumns,
    defaultTableType: () => props.tableType,
    defaultBreakdown: () => props.breakdown,
    matrixDimension: () => props.matrixDimension,
    pivotMetric: () => props.pivotMetric,
    allowedTableTypes: () => props.allowedTableTypes,
    namedViews: () => props.namedViews,
    userFilters: () => props.userFilters,
    enabled: stableViewEnabled,
    filtersEnabled: tableFeaturesEnabled,
    catalogSettled: chart.catalog.settled,
    catalogReady: computed(() => chart.catalog.status.value === 'ready'),
    catalogDefinitions: chart.catalog.definitions,
    dimensions: () => props.dimensions,
    metrics: () => props.metrics,
    activeChart: chart.chartType,
    onSort: (value) => emit('update:sort', value),
    onColumnOrder: (value) => emit('update:columnOrder', value),
    onColumnWidths: (value) => emit('update:columnWidths', value),
    onFilters: (value) => emit('update:userFilters', value),
    onTableType: (value) => emit('update:tableType', value),
    onBreakdown: (value) => emit('update:breakdown', value),
    onFrequency: (value) => emit('update:frequency', value),
    onRemoteActiveChart: chart.hydrateChartType,
  })
  // Requests break data by visible dimensions; metric toggles stay presentation-only.
  const visibleDimensionKey = computed(() => {
    const visible = new Set(
      presentedColumns.value.filter((column) => !column.invisible).map((column) => column.field),
    )
    return (props.requestDimensions ?? props.dimensions)
      .filter((field) => visible.has(field) || !props.dimensions.includes(field))
      .join('\n')
  })
  const tableDimensions = computed(() =>
    visibleDimensionKey.value === '' ? [] : visibleDimensionKey.value.split('\n'),
  )
  const activeSort = computed<ReportTableSortV1 | null>(() =>
    chartTable.value || chart.view.value === 'table' || chart.view.value === 'card'
      ? table.sort.value
      : props.sortField
        ? { field: props.sortField, order: props.sortOrder }
        : null,
  )
  const requestView = computed<ReportViewType>(() =>
    chartTable.value ? 'table' : chart.view.value,
  )
  const cardStatus = ref<ReportCardStatus>('all')
  const trendRequest = computed(() =>
    resolveDataVisibleTrendRequest(
      effectiveTableType.value,
      trendAssignment.value,
      presentation.frequency.value,
      props,
      !chart.catalog.settled.value || availableTableTypes.value.includes('table'),
    ),
  )
  /** Everything both requests of a `chart-table` surface state identically. */
  const sharedRequestOptions = () => ({
    reportId: () => props.reportId,
    partitionName: () => props.partitionName,
    defaultPostData: () => props.defaultPostData,
    chartCatalogLoading: chart.chartCatalogLoading,
    chartUnavailable: chart.chartUnavailable,
    chartFieldsValid: chart.fieldsValid,
    filters: computed(() => [...props.filters, ...activeUserFilters.value]),
    cardStatus,
    sort: activeSort,
    columns: presentedColumns,
    partitionOnly: () => props.partitionOnly,
    countUniqueDimensions: () => props.countUniqueDimensions,
    edges: () => props.edges,
    targetCurrency: () => props.targetCurrency,
    addCompare: () => props.addCompare,
  })
  const request = useDataVisibleReportRequest({
    ...sharedRequestOptions(),
    view: requestView,
    viewUnavailable: chart.viewUnavailable,
    tableUnavailable,
    // Exactly what the table presents: in `chart-table` a chart field the table does not show is
    // the chart's own question, not a wider shared one (`useDataVisibleChartRequest`).
    tableDimensions,
    tableMetrics: () => props.metrics,
    chartDimensions: chart.requestDimensions,
    chartTimeDimensions: chart.validationTimeDimensions,
    chartMetrics: chart.requestMetrics,
    // A `chart-table` table is a table: the chart's time role turns rows into one-parent-per-
    // category edge rows on the CHART's request, never on this one.
    chartActive: computed(() => !chartTable.value && chart.view.value === 'chart'),
    chartLimit: computed(() => chart.activeMeta.value?.defaultLimit),
    tableMode: () => trendRequest.value.tableMode,
    trend: () => trendRequest.value.trend,
    breakdownField: presentation.breakdownField,
    tablePresenterAvailable: computed(() => trendRequest.value.tablePresenterAvailable),
    pagination: () => props.pagination,
  })
  const spec = request.spec
  const workspaceIds = (): readonly (number | string)[] => props.workspaceIds ?? []
  const report = useReportData(spec, {
    workspaceIds,
    ...(props.dataLoader ? { dataLoader: props.dataLoader } : {}),
  })
  const ga4 = useGa4Override(report, getColumn)
  const { rows, renderColumns, expandableFields, activeExpandedFields } = useReportTableRender({
    reportId: () => props.reportId,
    rows: ga4.rows,
    columns: presentedColumns,
    expandedFields: presentation.expandedFields,
    sort: computed(() => (request.trend.value ? null : activeSort.value)),
    enabled: computed(
      () =>
        (chartTable.value || chart.view.value === 'table') &&
        effectiveTableType.value === 'table' &&
        request.trend.value === null,
    ),
    getColumn,
  })
  const chartRead = useDataVisibleChartRequest({
    enabled: chartTable,
    chart,
    shared: sharedRequestOptions(),
    tableSpec: spec,
    tableReport: report,
    tableRows: rows,
    tableBreakdownField: presentation.breakdownField,
    workspaceIds,
    ...(props.dataLoader ? { dataLoader: props.dataLoader } : {}),
  })
  const chartContract = useDataVisibleChartContract({
    chart,
    rows: chartRead.rows,
    rowLimit: computed(() => (chartTable.value ? chart.activeMeta.value?.defaultLimit : null)),
    response: chartRead.response,
    state: chartRead.state,
    currentSpec: computed(() => chartRead.spec.value ?? spec.value),
    settledSpec: chartRead.settledSpec,
    metricLabel,
    getColumn,
    formatValue: (value, field, returnStr) => formatCell(value, getColumn(field), returnStr),
    ...(props.channel ? { channel: props.channel } : {}),
  })
  const { value: renderValue, state: renderState } = useSettledReportRenderSnapshot({
    state: chartRead.surfaceState,
    currentSpec: chartRead.surfaceCurrentSpec,
    settledSpec: chartRead.surfaceSettledSpec,
    capture: () => ({
      view: requestView.value,
      viewUnavailable: chart.viewUnavailable.value,
      chartCatalogLoading: chart.chartCatalogLoading.value,
      chartUnavailable: chart.chartUnavailable.value,
      chartContractValid: chartContract.valid.value,
      rows: [...rows.value],
      columns: ga4.markColumns(renderColumns.value),
      trend: cloneResolvedTrend(request.trend.value),
      pagination: resolveReportPagination(
        request.pagination.value,
        report.response.value?.pagination?.total ?? report.response.value?.total_rows,
      ),
      chartSpec: chartContract.spec.value,
      total: ga4.total.value,
      totalPeriod: report.response.value?.total_period ?? null,
      otherTotal: report.response.value?.total_other ?? null,
      grandTotal: report.response.value?.grand_total ?? null,
      ga4Total:
        props.partitionName === 'budget_allocation' && activeUserFilters.value.length === 0
          ? (report.response.value?.total_ga4 ?? null)
          : null,
    }),
  })
  const state: ComputedRef<UiAsyncState> = computed(() => renderState.value)
  const refreshing = computed(
    () => chartRead.surfaceState.value === 'loading' && state.value !== 'loading',
  )
  const exportFlow = useDataVisibleExport({
    view: requestView,
    spec,
    settledSpec: computed(() => report.settledSpec?.value ?? spec.value),
    state: report.state,
    response: report.response,
    rows,
    reportId: () => props.reportId,
    tableColumns: renderColumns,
    tableType: effectiveTableType,
    breakdownField: presentation.breakdownField,
    matrixDimension: computed(() => matrixAssignment.value?.matrixDimension ?? null),
    pivotMetric: computed(() => matrixAssignment.value?.pivotMetric ?? null),
    trend: request.trend,
    sort: computed(() => (request.trend.value ? null : activeSort.value)),
    getColumn,
    name: () => props.exportName ?? props.ariaLabel,
    workspaceIds,
  })
  watch(chart.view, (value) => emit('update:view', value))
  watch(chart.chartType, (value) => emit('update:chartType', value))
  watch(fullscreen.error, (error) => {
    if (error) toast.error(t('reports.actions.fullscreenError'))
  })
  return {
    chartTable,
    fullscreen,
    chart,
    chartContract,
    report,
    request,
    exportFlow,
    state,
    refreshing,
    renderValue,
    table,
    presentation,
    presentedColumns,
    availableTableTypes,
    effectiveTableType,
    matrixAssignment,
    tableUnavailable,
    savedFilters,
    dropdownables,
    loadDropdownValues,
    savedViews,
    savedViewId,
    filterDraft,
    activeUserFilters,
    cardStatus,
    customizeOpen,
    expandableFields,
    activeExpandedFields,
    applyCustomization,
    updateFrequency,
    updateChartType: (value: ChartType) => chart.setChartType(value),
    updateTableType,
    updateBreakdown,
    applyFilters,
    saveFilter,
    removeFilter,
    saveNamedView,
    selectNamedView,
    renameNamedView,
    removeNamedView,
    updateTableSort,
    updateColumnOrder,
    updateColumnWidths,
    updateFrozenField,
    updateExpandedField,
  }
}
export type UseDataVisible = ReturnType<typeof useDataVisible>
